/**
 * Inline header (≥1000px): show full tab nav by default. Switch to compact (logo · cart · hamburger)
 * only when left or right pill rows **geometrically collide** with the wordmark (pill edges vs logo box).
 * Clipping/scroll heuristics were removed — they false-triggered on wide desktops (hidden scrollbar,
 * subpixel scrollWidth, ul vs nav rects) and forced compact while plenty of space remained.
 *
 * Hysteresis: after entering compact at width W, only return to full tabs when width ≥ W + EXIT_HYSTERESIS_PX.
 *
 * Toggles `hmc-header-inline-compact` on `header.header--inline` (assets/custom.css).
 */
(function () {
  'use strict';

  var DESK_MIN = 1000;
  var DEBOUNCE_MS = 48;
  /** After entering compact at width W, only return to full tabs when width ≥ W + this (px). */
  var EXIT_HYSTERESIS_PX = 72;
  /** Treat as collision when gap ≤ this (px); 1 allows subpixel touch without firing early on “almost”. */
  var COLLISION_EPS_PX = 1;

  function mqDesk() {
    return window.matchMedia('(min-width: ' + DESK_MIN + 'px)');
  }

  function navChainVisible(el) {
    var n = el;
    while (n && n !== document.body) {
      var cs = window.getComputedStyle(n);
      if (cs.display === 'none' || cs.visibility === 'hidden') return false;
      n = n.parentElement;
    }
    return true;
  }

  function getLogoBox(header) {
    var el = header.querySelector('.header__logo');
    return el ? el.getBoundingClientRect() : null;
  }

  /** Rightmost edge of left-nav pill labels (`<a>`), not `<li>` (grid can make `li` wider than the chip). */
  function leftNavContentRightEdge(header) {
    var list = header.querySelector('.nav-bar .nav-bar__linklist');
    if (!list || !navChainVisible(list)) return null;
    var links = list.querySelectorAll(':scope > .nav-bar__item > a.nav-bar__link');
    var maxRight = 0;
    var any = false;
    for (var i = 0; i < links.length; i++) {
      var r = links[i].getBoundingClientRect();
      if (r.width < 2 || r.height < 2) continue;
      any = true;
      maxRight = Math.max(maxRight, r.right);
    }
    return any ? maxRight : null;
  }

  /** Leftmost edge of right-nav pills (`<a>`). */
  function rightNavContentLeftEdge(header) {
    var list = header.querySelector('nav.hmc-nav-right .nav-bar__linklist');
    if (!list || !navChainVisible(list)) return null;
    var links = list.querySelectorAll(':scope > .nav-bar__item > a.nav-bar__link');
    var minLeft = Infinity;
    var any = false;
    for (var i = 0; i < links.length; i++) {
      var r = links[i].getBoundingClientRect();
      if (r.width < 2 || r.height < 2) continue;
      any = true;
      minLeft = Math.min(minLeft, r.left);
    }
    return any && minLeft !== Infinity ? minLeft : null;
  }

  /** True when left nav’s right edge (e.g. Help Center) touches or crosses the logo’s left edge. */
  function leftNavCollidesLogo(header) {
    var logo = getLogoBox(header);
    var rightEdge = leftNavContentRightEdge(header);
    if (!logo || rightEdge == null) return false;
    return logo.left - rightEdge <= COLLISION_EPS_PX;
  }

  /** True when right nav’s left edge touches or crosses the logo’s right edge. */
  function rightNavCollidesLogo(header) {
    var logo = getLogoBox(header);
    var leftEdge = rightNavContentLeftEdge(header);
    if (!logo || leftEdge == null) return false;
    return leftEdge - logo.right <= COLLISION_EPS_PX;
  }

  /** Only while full desktop nav is visible (not already compact). */
  function navNeedsCompactWhenExpanded(header) {
    var logo = getLogoBox(header);
    if (!logo || logo.width < 2) return false;
    return leftNavCollidesLogo(header) || rightNavCollidesLogo(header);
  }

  function initHeader(header) {
    if (!header.classList.contains('header--inline')) return;

    var inner = header.querySelector('.header__inner');
    if (!inner) return;

    var desk = mqDesk();
    var ro;
    var layoutDebounceTimer = null;
    var didFirstLayout = false;
    var layoutRunning = false;
    /** While true, ignore “no overlap” reads (nav is hidden). */
    var compactActive = false;
    /** innerWidth snapshot when we entered compact (hysteresis exit). */
    var widthAtCompactEnter = 0;

    function applyCompact(on) {
      compactActive = on;
      header.classList.toggle('hmc-header-inline-compact', on);
      /* When the header collapses to the hamburger on desktop, treat layouts as “mobile-like”. */
      if (document.body) {
        document.body.classList.toggle('hmc-desktop-nav-compact', on);
      }
      if (on) {
        widthAtCompactEnter = window.innerWidth;
      }
    }

    function syncClassFromState() {
      header.classList.toggle('hmc-header-inline-compact', compactActive);
    }

    function tryReExpandAfterWiden() {
      applyCompact(false);
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          if (!desk.matches) return;
          if (navNeedsCompactWhenExpanded(header)) {
            applyCompact(true);
          }
        });
      });
    }

    function layout() {
      if (layoutRunning) return;
      layoutRunning = true;
      try {
        if (!desk.matches) {
          if (compactActive) {
            applyCompact(false);
          }
          return;
        }

        var w = window.innerWidth;

        if (!compactActive) {
          if (navNeedsCompactWhenExpanded(header)) {
            applyCompact(true);
          }
        } else {
          if (w >= widthAtCompactEnter + EXIT_HYSTERESIS_PX) {
            tryReExpandAfterWiden();
          }
        }
      } finally {
        layoutRunning = false;
      }
    }

    function scheduleLayout() {
      if (!didFirstLayout) {
        didFirstLayout = true;
        window.requestAnimationFrame(layout);
        return;
      }
      if (layoutDebounceTimer) window.clearTimeout(layoutDebounceTimer);
      layoutDebounceTimer = window.setTimeout(function () {
        layoutDebounceTimer = null;
        window.requestAnimationFrame(layout);
      }, DEBOUNCE_MS);
    }

    desk.addEventListener('change', function (e) {
      if (e.matches) {
        var menu = document.getElementById('mobile-menu');
        if (menu && menu.getAttribute('aria-hidden') === 'false') {
          var btn = document.querySelector(
            '.header__mobile-nav [data-action="toggle-menu"][aria-controls="mobile-menu"]'
          );
          if (btn) btn.click();
        }
      }
      scheduleLayout();
    });
    window.addEventListener('resize', scheduleLayout);
    window.addEventListener('orientationchange', scheduleLayout);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scheduleLayout);
    }

    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(scheduleLayout);
      ro.observe(inner);
      var logoImg = header.querySelector('.header__logo-image');
      if (logoImg) ro.observe(logoImg);
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        scheduleLayout();
      });
    }

    scheduleLayout();
    /* Grid + webfonts: first frame can mis-measure; re-run once layout has settled. */
    window.setTimeout(scheduleLayout, 160);
  }

  function boot() {
    document.querySelectorAll('header.header').forEach(initHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
